import {
  SHAPE_GROUPS,
  SHAPE_KINDS,
  shapeDef,
  shapeSvg,
  type DesignShapeLayer,
  type ShapeKind,
} from '@pz/scene';
import { chipRow, el, field, note, type DesignerCtx } from './panels.js';
import { createColorBlock, createStepper } from './controls.js';
import { run } from './commands.js';
import { canAddLayer, freshShapeLayer, round, type LayerPatch } from './state.js';
import { fillText } from './texts.js';

/**
 * Oblici — the shape catalogue and the controls for the shape in hand.
 *
 * The panel itself is now part of "Elementi" (that is where the reference
 * editor keeps shapes), but the pieces live here so both the desktop rail and
 * a phone's Add drawer build the same grids from the same tiles.
 *
 * `SHAPE_KINDS` is a closed list on purpose: a shape id travels inside a paid
 * order, so entries are added and never renamed.
 */

/** The five the reference offers above the catalogue, as "Dynamic Shapes". */
export const DYNAMIC_SHAPES: ShapeKind[] = ['rect', 'roundRect', 'ellipse', 'triangle'];

export function allowedShapes(ctx: DesignerCtx): ShapeKind[] {
  return ctx.config.shape.kinds.length ? ctx.config.shape.kinds : SHAPE_KINDS;
}

/** Every gallery tile adds a new shape and selects it. */
export function shapeTile(ctx: DesignerCtx, kind: ShapeKind): HTMLElement {
  const selected = ctx.selected();
  const shape = selected?.kind === 'shape' ? selected : undefined;
  const def = shapeDef(kind);
  const tile = el('button', `pzd-tile${shape?.shape === kind ? ' is-on' : ''}`);
  tile.type = 'button';
  tile.setAttribute('aria-label', def.labelSr);
  tile.title = def.labelSr;
  tile.innerHTML = shapeSvg(kind, { size: 34, fill: '#3c3c48' });
  tile.addEventListener('click', () => {
    const room = canAddLayer(ctx.store.state(), ctx.config, 'shape');
    if (!room.ok) { ctx.toast(fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'error'); return; }
    ctx.dispatch({ type: 'add', layer: freshShapeLayer(ctx.rawConfig, kind, ctx.addCount()) });
    ctx.refreshPanel();
  });
  return tile;
}

/** The dynamic row, including the flat rectangle that stands in for a line. */
export function renderDynamicShapes(ctx: DesignerCtx): HTMLElement {
  const allowed = allowedShapes(ctx);
  const box = field(ctx.texts.dynamicShapes);
  const grid = el('div', 'pzd-grid');
  for (const kind of DYNAMIC_SHAPES) {
    if (!allowed.includes(kind)) continue;
    grid.append(shapeTile(ctx, kind));
  }
  if (allowed.includes('rect')) {
    const line = el('button', 'pzd-tile');
    line.type = 'button';
    line.setAttribute('aria-label', ctx.texts.stroke);
    line.title = ctx.texts.stroke;
    line.innerHTML =
      '<svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">'
      + '<rect x="2" y="11" width="20" height="2" rx="1" fill="#3c3c48"/></svg>';
    line.addEventListener('click', () => {
      const room = canAddLayer(ctx.store.state(), ctx.config, 'shape');
      if (!room.ok) { ctx.toast(fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'error'); return; }
      const layer = freshShapeLayer(ctx.rawConfig, 'rect', ctx.addCount());
      ctx.dispatch({
        type: 'add',
        layer: { ...layer, box: { ...layer.box, w: round(ctx.config.sizeMm.w * 0.5, 2), h: 1 } },
      });
      ctx.refreshPanel();
    });
    grid.append(line);
  }
  box.wrap.append(grid);
  return box.wrap;
}

/** Every catalogue group that has shapes this zone allows. */
export function renderShapeGroups(ctx: DesignerCtx): HTMLElement[] {
  const allowed = allowedShapes(ctx);
  const out: HTMLElement[] = [];
  for (const group of SHAPE_GROUPS) {
    const kinds = allowed.filter((kind) => shapeDef(kind).group === group.id);
    if (!kinds.length) continue;
    const box = field(group.labelSr);
    const grid = el('div', 'pzd-grid');
    for (const kind of kinds) grid.append(shapeTile(ctx, kind));
    box.wrap.append(grid);
    out.push(box.wrap);
  }
  return out;
}

/** Fill, line and the per-layer chips for the shape currently selected. */
export function renderShapeControls(ctx: DesignerCtx, layer: DesignShapeLayer): HTMLElement {
  const cfg = ctx.config;
  const id = layer.id;
  const wrap = el('div');
  const patch = (changes: LayerPatch): void => {
    ctx.dispatch({ type: 'update', id, patch: changes });
  };
  const strokeWidth = layer.stroke?.widthMm ?? 0;

  wrap.append(createColorBlock({
    ctx,
    title: ctx.texts.fill,
    value: layer.fill,
    palette: cfg.shape.palette,
    // A shape with neither fill nor stroke is refused by the validator, so
    // "no fill" is only offered once there is a line to see it by.
    allowNone: strokeWidth > 0,
    onPick: (color) => patch({ fill: color }),
  }).element);

  wrap.append(createColorBlock({
    ctx,
    title: ctx.texts.stroke,
    value: layer.stroke?.color,
    palette: cfg.shape.palette,
    allowNone: true,
    onPick: (color) => {
      if (!color) {
        if (!layer.fill) {
          // Removing the only ink would make the layer invisible and invalid.
          patch({ stroke: undefined, fill: cfg.shape.palette[0] ?? { hex: '#000000' } });
          return;
        }
        patch({ stroke: undefined });
        return;
      }
      patch({ stroke: { color, widthMm: strokeWidth || 0.5 } });
    },
  }).element);

  wrap.append(createStepper({
    label: ctx.texts.lineWeight,
    value: strokeWidth,
    min: 0,
    max: 20,
    step: 0.1,
    unit: 'mm',
    slider: true,
    onCommit: (widthMm) => {
      if (widthMm <= 0) {
        patch({ stroke: undefined, ...(layer.fill ? {} : { fill: cfg.shape.palette[0] ?? { hex: '#000000' } }) });
        return;
      }
      patch({ stroke: { color: layer.stroke?.color ?? cfg.shape.palette[0] ?? { hex: '#000000' }, widthMm } });
    },
  }).element);

  wrap.append(chipRow([
    { id: 'duplicate', label: ctx.texts.duplicate, icon: 'copy' },
    { id: 'remove', label: ctx.texts.remove, icon: 'trash' },
  ], (action) => {
    run(ctx, { op: action === 'duplicate' ? 'duplicate' : 'delete', ids: [id] });
  }));

  return wrap;
}

/**
 * The stand-alone Oblici panel. The desktop rail no longer shows it — shapes
 * live under Elementi — but the phone's Add drawer and any host that opens the
 * `shape` tool by name still land here.
 */
export function renderShapePanel(body: HTMLElement, ctx: DesignerCtx): void {
  const selected = ctx.selected();
  if (selected?.kind === 'shape') body.append(renderShapeControls(ctx, selected));

  const room = canAddLayer(ctx.store.state(), ctx.config, 'shape');
  if (!room.ok) {
    body.append(note(fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'warn'));
    return;
  }
  body.append(renderDynamicShapes(ctx));
  for (const group of renderShapeGroups(ctx)) body.append(group);
}
